import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { API_URLS } from "../../config/apiUrls";

interface Categoria {
  id: string;
  name: string;
  segment: string;
}

interface CategoriasProps {
  token: string;
  shouldUpdateListAdd: boolean;
  atualizarCategorias: () => void;
}

const CategoriasAdicionar: React.FC<CategoriasProps> = ({
  token,
  atualizarCategorias,
  shouldUpdateListAdd,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [novaSubcategoria, setNovaSubcategoria] = useState("");
  const [categoriaSelecionada, setCategoriaSelecionada] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCategorias = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(API_URLS.categorias);
        if (!response.ok) throw new Error("Não foi possível carregar as categorias.");
        const data = (await response.json()) as Categoria[];
        setCategorias(data);
      } catch (error) {
        console.error("Erro ao buscar categorias:", error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchCategorias();
  }, [shouldUpdateListAdd]);

  const adicionarSubcategoria = async () => {
    const name = novaSubcategoria.trim();
    if (!name || !categoriaSelecionada) return;

    setIsLoading(true);
    try {
      const response = await fetch(API_URLS.subcategorias, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, categoryId: categoriaSelecionada }),
      });
      if (!response.ok) throw new Error("Não foi possível criar a subcategoria.");

      setNovaSubcategoria("");
      await atualizarCategorias();
    } catch (error) {
      console.error("Erro ao criar subcategoria:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Button
        variant="contained"
        color={showForm ? "secondary" : "primary"}
        startIcon={<AddIcon />}
        onClick={() => setShowForm((visible) => !visible)}
      >
        {showForm ? "Ocultar formulário" : "Adicionar subcategoria"}
      </Button>

      <Collapse in={showForm}>
        <Box
          sx={{
            maxWidth: 640,
            mt: 3,
            p: 3,
            border: "1px solid #ddd",
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Nova subcategoria
          </Typography>
          <FormControl fullWidth disabled={isLoading} sx={{ mb: 2 }}>
            <InputLabel>Categoria fixa</InputLabel>
            <Select
              value={categoriaSelecionada}
              onChange={(event) => setCategoriaSelecionada(event.target.value)}
              label="Categoria fixa"
            >
              {categorias.map((categoria) => (
                <MenuItem key={categoria.id} value={categoria.id}>
                  {categoria.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Nome da subcategoria"
            value={novaSubcategoria}
            onChange={(event) => setNovaSubcategoria(event.target.value)}
            fullWidth
            disabled={isLoading || !categoriaSelecionada}
            sx={{ mb: 2 }}
          />
          <Button
            onClick={adicionarSubcategoria}
            variant="contained"
            disabled={isLoading || !categoriaSelecionada || !novaSubcategoria.trim()}
            fullWidth
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Adicionar"}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CategoriasAdicionar;
